import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {UrlService} from "app/core/services/url.service";
import {ErrorHandlingService} from "app/core/services/error.handling.service";
import {PermissionsService} from "app/core/services/permissions.service";
import {BehaviorSubject, Observable, tap} from "rxjs";
import {UrlEnum} from "app/core/types/url-enum";
import {ObservableErrHandler} from "app/core/types/err.handling.types";
import {AppModuleRsp, FeatModule, getFeatureMap} from "./feat-mgmt.types";
import {DateTimeService} from "../../../../core/services/date.time.service";
import {DateTime, Duration} from "luxon";
import {formatDate} from "@angular/common";
import {InterventionModel, InterventionModelToCreate} from "../../../patient/interventions/intervention.types";

@Injectable({providedIn: 'root'})
export class FeatMgmtService
{
    private readonly _urlService: UrlService = inject(UrlService);
    private readonly _errHandlService: ErrorHandlingService = inject(ErrorHandlingService);
    private readonly _httpClient: HttpClient = inject(HttpClient);
    private readonly _permissionSrv: PermissionsService = inject(PermissionsService);
    private readonly _dts: DateTimeService = inject(DateTimeService);
    private readonly _moduleData: BehaviorSubject<FeatModule[]> = new BehaviorSubject([]);
    private readonly _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);

    get busy$() {
        return this._busy.asObservable();
    }

    /**
     * Constructor
     */
    constructor() {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */
    get data$(): Observable<FeatModule[]> {
        return this._moduleData.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get data
     */
    getModuleInfoFromBE(): void
    {
        if(!this._permissionSrv.isTenantAdmin() && !this._permissionSrv.isTenantOwner()){
            return;
        }

        if(this._moduleData.value && this._moduleData.value.length > 0){
            //do not get new data if data exists
            return;
        }

        this._busy.next(true);
        const obs = this._httpClient.get<AppModuleRsp[]>(
            this._urlService.getUrl(UrlEnum.private_get_app_module));

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            err_message: "There was a problem getting app modules for clinic."
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: value => {
                    //map to internal data and anounce new data
                    const allModules:Map<string, FeatModule> = getFeatureMap();
                    let featModules:FeatModule[]  = [];
                    value.sort((a,b) => {
                        if(a.active && b.active){
                            return a.internalPrefixCode.localeCompare(b.internalPrefixCode);
                        }
                        if(a.active && !b.active){
                            return -1;
                        }
                        if(!a.active && b.active){
                            return 1;
                        }
                        return 0;
                    });
                    value.forEach(feat => {
                        const fm : FeatModule = allModules.get(feat.internalPrefixCode);
                        if(fm) {
                            fm.id = feat.id;
                            //fm.name = feat.name;
                            fm.price = feat.price;
                            fm.expirationDate = feat.expirationDate;
                            fm.active = feat.active;
                            fm.endDate = this._dts.getDateAndTimeFromBEInDateObject(feat.expirationDate);
                            fm.purchased = fm.endDate > new Date() && fm.active;
                            fm.partOfSubscription = feat.partOfSubscription;
                            featModules.push(fm);
                        }else {
                            console.log("Data sync error: Feature not found: " + feat.internalPrefixCode);
                        }
                    })
                    this._moduleData.next(featModules);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));

        errWrapper.observable.subscribe();
    }

    public updateModuleSubscription(moduleID:string, activate:boolean):void {
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.put<any>(
            this._urlService
                .getUrl(UrlEnum.private_update_app_module)
                .replace("{id}", moduleID)
                .replace("{value}", activate.toString())
            , JSON.stringify(null), {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: intervention => {
                    // update module
                    this._moduleData.value.forEach(module => {
                        if(module.id == moduleID){
                            module.partOfSubscription = activate;
                            this._moduleData.next(this._moduleData.value);
                        }
                    })
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    getTimeUntilExpirationAsString(module: FeatModule): string {
        if(this._dts.dateInPast(module.endDate)){
            const expDate:string = formatDate(module.endDate, 'dd MMM yyyy', 'ro-RO');
            return `Expirat la ${expDate}`
        }

        return this._dts.getDurationAsStringBetweenDates(new Date(), module.endDate);
    }

    moduleExpiresSoon(module: FeatModule): boolean {
        const duration = this._dts.getDurationBetweenDates(new Date(),module.endDate);
        return module.purchased && duration?.years < 1 && duration?.months < 1 && duration?.days < 7 && duration?.days > 0;
    }


    public getModulesThatExpiresSoonOrExpired(): FeatModule[] {
        if(!(this._moduleData?.value?.length > 0)){
            return null;
        }
        const modules:FeatModule[] = this._moduleData.value.concat([]);
        modules.sort((a,b) => b.endDate.getTime() - a.endDate.getTime() );
        return modules.filter(module => this.moduleExpiresSoon(module));
    }

}
